import React, { useEffect, useState } from "react";
import {
  PermissionsEnum,
  ProfessionalHistoryMenuEnum,
  professionalHistoryMenuLabels,
} from "../../../../constants/AppConstants";
import { CollaboratorProfile } from "./CollaboratorProfile/CollaboratorProfile";
import {
  ProfessionalHistoryHeader,
  StyledProfessionalHistory,
  StyledProfessionalHistoryContent,
} from "./ProfessionalHistory.styled";
import { FormationHistory } from "./Formation/FormationHistory";
import { ProjectsHistory } from "./Projects/ProjectsHistory";
import { TitlesTyp } from "../../../../components/Typography/Typography";
import { FontWeightEnum } from "../../../../types/AppTypes";
import { SkillsHistory } from "./Skills/SkillsHistory";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
  CollaboratorHistoryApprovalStatus,
} from "../../../../types/api/collaborators.type";
import { ProfessionalPortrait } from "./ProfessionalPortrait/ProfessionalPortrait";
import { StyledExpandMenuContent } from "../CollaboratorProfileTabs.styled";
import { FooterHistoryApproval } from "./components/FooterHistoryApproval/FooterHistoryApproval";
import { ConfirmHistoryApproval } from "./components/ConfirmHistoryApproval";
import MessageChip from "../../../../components/MessageChip/MessageChip";
import { theme } from "../../../../constants/StyledComponents";
import { ReactComponent as HourglassIcon } from "../../../../icons/hourglass.svg";
import { ReactComponent as CheckIcon } from "../../../../icons/check.svg";
import { useAppSelector } from "../../../../hooks/store.hook";
import { CV_STATUS_LABEL } from "../../../../constants/Dictionary";
import { roleHasPermission } from "../../../../utils/roleAuth.utils";
import { dateFormat } from "../../../../constants/TableColumns";
import { useConfirmApprovePersonalProfile } from "../../../../hooks/confirmApprovePersonalProfile.hooks";

type ProfessionalHistoryProps = {
  collaborator: CollaboratorDetails;
  selectedMenu: ProfessionalHistoryMenuEnum;
  currentCmSheet?: CollaboratorCmSheet;
  isMyProfile?: boolean;
  approvalStatusData: CollaboratorHistoryApprovalStatus | null;
  refetchStatus: () => Promise<void>;
  onAdvancementsRefresh?: () => void;
};

export const ProfessionalHistory = ({
  collaborator,
  selectedMenu,
  isMyProfile,
  currentCmSheet,
  approvalStatusData,
  refetchStatus,
  onAdvancementsRefresh,
}: ProfessionalHistoryProps) => {
  const [link, setLink] = useState<React.ReactNode | null>(null);
  const [textSwitch, setTextSwitch] = useState<React.ReactNode | null>(null);
  const TEXT_SWITCH_MENUS = [
    ProfessionalHistoryMenuEnum.SKILLS,
    ProfessionalHistoryMenuEnum.FORMATION,
  ];

  const { profile } = useAppSelector((state) => state.user);
  const profileId = collaborator.id;

  const [historyRefetchFn, setHistoryRefetchFn] =
    useState<CallableFunction | null>(null);

  const pendingItemsCount =
    approvalStatusData?.totalSectionsCounts.pending ?? 0;
  const draftItemsCount = approvalStatusData?.totalSectionsCounts.draft ?? 0;

  const { mutateAsync, isPending } = useConfirmApprovePersonalProfile();

  const handleBtnPersonalProfile = async () => {
    await mutateAsync({ profileId, isMyProfile: !!isMyProfile });
    await refetchStatus();
    historyRefetchFn?.();
    !isMyProfile && onAdvancementsRefresh?.();
  };

  const showFooter =
    selectedMenu !== ProfessionalHistoryMenuEnum.COLLABORATOR_PROFILE;

  useEffect(() => {
    if (!TEXT_SWITCH_MENUS.includes(selectedMenu)) {
      setTextSwitch(null);
    }
  }, [selectedMenu]);

  const showApproveCv = (): boolean => {
    if (currentCmSheet?.careerManagerId === profile.id) {
      return true;
    }

    if (
      roleHasPermission(profile, PermissionsEnum.APPROVE_COLLABORATOR_CV) &&
      collaborator?.terminationDate === null
    ) {
      return true;
    }

    return false;
  };

  const showChipCv = () => {
    if (!isMyProfile && approvalStatusData?.mostRecentCVGenerationDate) {
      if (currentCmSheet?.careerManagerId === profile.id) {
        return true;
      }
      if (
        roleHasPermission(
          profile,
          PermissionsEnum.CAN_DOWNLOAD_CV_COLLABORATOR_PROFILE,
        ) &&
        collaborator?.terminationDate === null
      ) {
        return true;
      }
    }

    return false;
  };

  const canShowButton = isMyProfile || showApproveCv();
  const isDisabled = isMyProfile
    ? draftItemsCount === 0
    : pendingItemsCount === 0;

  return (
    <StyledExpandMenuContent>
      <StyledProfessionalHistory>
        <ProfessionalHistoryHeader>
          <div className="title-switch">
            <TitlesTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
              {professionalHistoryMenuLabels[selectedMenu]}
            </TitlesTyp>
            {textSwitch}
          </div>
          {link}
        </ProfessionalHistoryHeader>
        <StyledProfessionalHistoryContent>
          {selectedMenu ===
            ProfessionalHistoryMenuEnum.COLLABORATOR_PROFILE && (
            <CollaboratorProfile
              currentCmSheet={currentCmSheet ?? null}
              isMyProfile={isMyProfile}
              collaborator={collaborator}
            />
          )}
          {selectedMenu ===
            ProfessionalHistoryMenuEnum.PROFESSIONAL_PORTRAIT && (
            <ProfessionalPortrait
              collaborator={collaborator}
              isMyProfile={isMyProfile}
              onStatusRefresh={refetchStatus}
              setHistoryRefetchFn={setHistoryRefetchFn}
              currentCmSheet={currentCmSheet}
            />
          )}
          {selectedMenu === ProfessionalHistoryMenuEnum.PROJECTS && (
            <ProjectsHistory
              collaborator={collaborator}
              isMyProfile={isMyProfile}
              setLink={setLink}
              onStatusRefresh={refetchStatus}
              currentCmSheet={currentCmSheet}
            />
          )}
          {selectedMenu === ProfessionalHistoryMenuEnum.FORMATION && (
            <FormationHistory
              collaborator={collaborator}
              isMyProfile={isMyProfile}
              setLink={setLink}
              setTextSwitch={setTextSwitch}
              onStatusRefresh={refetchStatus}
              currentCmSheet={currentCmSheet}
            />
          )}
          {selectedMenu === ProfessionalHistoryMenuEnum.SKILLS && (
            <SkillsHistory
              collaborator={collaborator}
              setTextSwitch={setTextSwitch}
              setLink={setLink}
              isMyProfile={isMyProfile}
              onStatusRefresh={refetchStatus}
              setHistoryRefetchFn={setHistoryRefetchFn}
              currentCmSheet={currentCmSheet}
            />
          )}
        </StyledProfessionalHistoryContent>
      </StyledProfessionalHistory>
      {showFooter && (
        <FooterHistoryApproval
          leftContent={
            !isMyProfile && pendingItemsCount > 0 ? (
              <MessageChip
                icon={<HourglassIcon />}
                color={theme.colors.yellow_2}
                message={`${pendingItemsCount} elementi da approvare`}
              />
            ) : showChipCv() ? (
              <MessageChip
                icon={<CheckIcon />}
                color={theme.colors.green_2}
                message={`${CV_STATUS_LABEL}: aggiornato il ${dateFormat(approvalStatusData!.mostRecentCVGenerationDate)}`}
              />
            ) : null
          }
          rightContent={
            canShowButton ? (
              <ConfirmHistoryApproval
                isMyProfile={isMyProfile}
                disabled={isDisabled}
                onConfirm={handleBtnPersonalProfile}
                isLoading={isPending}
              />
            ) : null
          }
        />
      )}
    </StyledExpandMenuContent>
  );
};
